import { ERROR_NOISE_PATTERNS } from '@mondaygpt/constants'
import type { PosthogLogLevel } from '../posthog/posthog-logs'
import { posthogClient } from '../posthog/posthog.service'
import { captureText, routeClientCapture, type ClientCapture } from './route'

/**
 * The browser's error-reporting API. Every capture lands in PostHog: errors as
 * error-tracking issues (posthog-js `captureException`, linked to the session
 * replay), warning/info captures as PostHog Logs records (service `client`).
 * Globally-unhandled errors and rejections are captured natively by posthog-js
 * (`capture_exceptions`), not here.
 *
 * Scopes: one global scope (build, environment, breadcrumbs) and a stack of
 * temporary scopes pushed by `withScope` for a single capture. The user is not
 * part of it: posthog-js `identify()` already attributes every capture, log
 * line (`distinct_id`) and span (`posthogDistinctId`) to the person.
 */

export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
type Primitive = string | number | boolean | null | undefined

export interface Breadcrumb {
  category?: string
  message?: string
  level?: SeverityLevel
  data?: Record<string, unknown>
  timestamp?: number
}

export interface CaptureContext {
  level?: SeverityLevel
  tags?: Record<string, Primitive>
  extra?: Record<string, unknown>
  fingerprint?: readonly unknown[]
}

const MAX_BREADCRUMBS = 100

export class Scope {
  tags: Record<string, Primitive> = {}
  extra: Record<string, unknown> = {}
  breadcrumbs: Breadcrumb[] = []

  setTag(key: string, value: Primitive): this {
    if (value === null || value === undefined) delete this.tags[key]
    else this.tags[key] = value
    return this
  }

  setExtra(key: string, value: unknown): this {
    this.extra[key] = value
    return this
  }

  addBreadcrumb(breadcrumb: Breadcrumb): this {
    this.breadcrumbs.push({ timestamp: Date.now() / 1000, ...breadcrumb })
    if (this.breadcrumbs.length > MAX_BREADCRUMBS) {
      this.breadcrumbs.splice(0, this.breadcrumbs.length - MAX_BREADCRUMBS)
    }
    return this
  }
}

const globalScope = new Scope()
const scopeStack: Scope[] = []

/** Run `callback` with a temporary scope whose tags/extras apply only to captures made inside it. */
export function withScope(callback: (scope: Scope) => void): void {
  const scope = new Scope()
  scopeStack.push(scope)
  try {
    callback(scope)
  } finally {
    scopeStack.pop()
  }
}

export function setTag(key: string, value: Primitive): void {
  globalScope.setTag(key, value)
}

export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  globalScope.addBreadcrumb(breadcrumb)
}

// The same Error reported by several layers (an ErrorBoundary plus React's
// onCaughtError, a store plus its component) mints one issue. Remember the lane
// each Error went to: once it is an issue, later captures are dropped; if it
// only reached Logs (captured at warning), an issue-level capture still goes
// through.
type Lane = 'issue' | 'log'
const capturedLane = new WeakMap<object, Lane>()

function isNoise(text: string): boolean {
  return ERROR_NOISE_PATTERNS.some(pattern => pattern.test(text))
}

function build(
  input: Pick<ClientCapture, 'exception' | 'message' | 'mechanism'>,
  context: CaptureContext | undefined
): ClientCapture {
  const capture: ClientCapture = {
    ...input,
    level: context?.level,
    tags: { ...globalScope.tags },
    extra: { ...globalScope.extra },
    breadcrumbs: globalScope.breadcrumbs.slice(-MAX_BREADCRUMBS),
    fingerprint: context?.fingerprint,
  }
  for (const scope of scopeStack) {
    Object.assign(capture.tags, scope.tags)
    Object.assign(capture.extra, scope.extra)
  }
  Object.assign(capture.tags, context?.tags)
  Object.assign(capture.extra, context?.extra)

  // Tag react-refresh / HMR errors as dev-only for easier filtering
  const stack = input.exception instanceof Error ? (input.exception.stack ?? '') : ''
  if (stack.includes('react-refresh')) capture.tags.dev_only = 'true'
  return capture
}

/** Sends the capture; returns the lane it went to, or undefined when dropped. */
function send(capture: ClientCapture, issueOnly = false): Lane | undefined {
  try {
    if (isNoise(captureText(capture))) return undefined
    const route = routeClientCapture(capture)
    if (route.lane === 'issue') {
      posthogClient.captureException(route.error, route.properties)
      return 'issue'
    }
    if (route.lane === 'log' && !issueOnly) {
      posthogClient.shipLog(route.level, route.message, route.attributes)
      return 'log'
    }
  } catch {
    // Telemetry must never break the caller. No console.* here: the console
    // bridge would route it straight back into this function.
  }
  return undefined
}

export function captureException(
  exception: unknown,
  context?: CaptureContext,
  mechanism?: string
): void {
  const key = exception !== null && typeof exception === 'object' ? exception : undefined
  const previous = key ? capturedLane.get(key) : undefined
  if (previous === 'issue') return
  const lane = send(build({ exception, mechanism }, context), previous === 'log')
  if (key && lane) capturedLane.set(key, lane)
}

export function captureMessage(
  message: string,
  levelOrContext?: SeverityLevel | CaptureContext
): void {
  const context = typeof levelOrContext === 'string' ? { level: levelOrContext } : levelOrContext
  send(build({ message }, context))
}

/**
 * Structured log lines straight to PostHog Logs (never the error inbox, never
 * the console, which the console bridge would forward a second time).
 */
function logAt(level: PosthogLogLevel) {
  return (message: string, attributes: Record<string, unknown> = {}): void => {
    try {
      posthogClient.shipLog(level, message, { ...globalScope.tags, ...attributes })
    } catch {
      // Same loop hazard as send(): never console.* from here
    }
  }
}

export const logger = {
  debug: logAt('debug'),
  info: logAt('info'),
  warn: logAt('warn'),
  error: logAt('error'),
}
